import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { KeyRound, Plus, Trash2, Ban, Copy, AlertTriangle, ShieldOff, BookOpen, Play, Code2, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ApiKeysTab = ({ language }) => {
  const isEs = language === 'es';
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showWarning, setShowWarning] = useState(false); // confirmation before sending
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState({ name: '', partner_email: '', commission_rate: 0, notes: '' });
  const [revealedKey, setRevealedKey] = useState(null); // {raw_key, name, email_sent, email_error}
  const [showDocs, setShowDocs] = useState(false);
  const [docsKey, setDocsKey] = useState(''); // sample key to embed in examples
  const [testResp, setTestResp] = useState(''); // result of "Try it"
  const [testLoading, setTestLoading] = useState(false);

  const loadKeys = async () => {
    try {
      const r = await axios.get(`${API}/admin/api-keys`);
      setKeys(r.data || []);
    } catch (e) {
      toast.error('Error cargando API keys');
    }
  };

  useEffect(() => { loadKeys(); }, []);

  // STEP 1: validate inputs, then open warning confirmation
  const handleOpenWarning = () => {
    if (!newKey.name.trim()) {
      toast.error('El nombre del socio es obligatorio');
      return;
    }
    if (!newKey.partner_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newKey.partner_email)) {
      toast.error('Introduce un email válido del socio (a donde se enviará el acceso)');
      return;
    }
    setShowWarning(true);
  };

  // STEP 2: confirmed → create key AND send email
  const handleConfirmAndSend = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/admin/api-keys`, { ...newKey, send_email: true });
      setRevealedKey({
        raw_key: r.data.raw_key,
        name: r.data.name,
        email_sent: r.data.email_sent,
        email_error: r.data.email_error,
        partner_email: newKey.partner_email,
      });
      setDocsKey(r.data.raw_key);
      setNewKey({ name: '', partner_email: '', commission_rate: 0, notes: '' });
      setShowWarning(false);
      setShowCreate(false);
      if (r.data.email_sent) {
        toast.success(`Email enviado a ${r.data.name}`);
      } else {
        toast.error('Clave creada, pero el email NO se envió. Copia el enlace manualmente.');
      }
      await loadKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando clave');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm(isEs ? '¿Revocar esta key? El partner perderá acceso inmediatamente.' : 'Revoke this key? The partner loses access immediately.')) return;
    try {
      await axios.post(`${API}/admin/api-keys/${id}/revoke`);
      toast.success(isEs ? 'Key revocada' : 'Key revoked');
      loadKeys();
    } catch (e) {
      toast.error('Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isEs ? '¿Eliminar definitivamente esta key?' : 'Delete this key permanently?')) return;
    try {
      await axios.delete(`${API}/admin/api-keys/${id}`);
      toast.success(isEs ? 'Key eliminada' : 'Key deleted');
      loadKeys();
    } catch (e) {
      toast.error('Error');
    }
  };

  const copyKey = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(isEs ? 'Copiada al portapapeles' : 'Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Public Portal banner */}
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-emerald-800 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              {isEs ? 'Portal público para partners' : 'Public partner portal'}
            </div>
            <div className="text-sm text-emerald-700">
              {isEs
                ? 'Envíale a tu partner este enlace + su API key. Entra, descarga el Excel y listo. Sin instalar nada.'
                : 'Send your partner this link + their API key. They log in and download the Excel. Zero setup.'}
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <code className="flex-1 sm:flex-initial bg-white border border-emerald-200 px-3 py-2 rounded text-sm font-mono select-all">
              {`${window.location.origin}/partner`}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyKey(`${window.location.origin}/partner`)}
              data-testid="copy-portal-url"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={() => window.open(`${window.location.origin}/partner`, '_blank')}
              data-testid="open-portal"
            >
              {isEs ? 'Abrir' : 'Open'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-indigo-600" />
                {isEs ? 'API Keys de Partners' : 'Partner API Keys'}
              </CardTitle>
              <CardDescription>
                {isEs
                  ? 'Genera y gestiona claves de acceso para socios revendedores que consumen tu catálogo vía API.'
                  : 'Generate and manage access keys for reseller partners consuming your catalog via API.'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowDocs(true)} data-testid="open-docs-btn">
                <BookOpen className="h-4 w-4 mr-1" />
                {isEs ? 'Cómo usar' : 'How to use'}
              </Button>
              <Button onClick={() => setShowCreate(true)} data-testid="create-api-key-btn">
                <Plus className="h-4 w-4 mr-1" />
                {isEs ? 'Nueva Key' : 'New Key'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isEs ? 'Nombre' : 'Name'}</TableHead>
                <TableHead>{isEs ? 'Prefijo' : 'Prefix'}</TableHead>
                <TableHead>{isEs ? 'Email Partner' : 'Partner Email'}</TableHead>
                <TableHead className="text-right">{isEs ? 'Comisión %' : 'Commission %'}</TableHead>
                <TableHead>{isEs ? 'Estado' : 'Status'}</TableHead>
                <TableHead>{isEs ? 'Último uso' : 'Last used'}</TableHead>
                <TableHead className="text-right">{isEs ? 'Acciones' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {isEs ? 'No hay API keys creadas todavía' : 'No API keys created yet'}
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((k) => (
                  <TableRow key={k.id} data-testid={`api-key-row-${k.key_prefix}`}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs">{k.key_prefix}...</TableCell>
                    <TableCell className="text-sm">{k.partner_email || '—'}</TableCell>
                    <TableCell className="text-right">{(k.commission_rate || 0).toFixed(2)}%</TableCell>
                    <TableCell>
                      {k.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700">{isEs ? 'Activa' : 'Active'}</Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700">{isEs ? 'Revocada' : 'Revoked'}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleString('es-ES') : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {k.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700"
                            title={isEs ? 'Revocar' : 'Revoke'}
                            onClick={() => handleRevoke(k.id)}
                            data-testid={`revoke-${k.key_prefix}`}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700"
                          title={isEs ? 'Eliminar' : 'Delete'}
                          onClick={() => handleDelete(k.id)}
                          data-testid={`delete-${k.key_prefix}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-indigo-100 bg-indigo-50/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Code2 className="h-4 w-4 text-indigo-600" />
              {isEs ? 'Endpoints disponibles para partners' : 'Available endpoints for partners'}
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setShowDocs(true)} data-testid="open-docs-inline">
              <BookOpen className="h-4 w-4 mr-1" />
              {isEs ? 'Ver guía completa' : 'Full guide'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-2 font-mono">
          <div><span className="text-emerald-700">GET</span> <code className="text-slate-700">{`${API}/external/products`}</code> — {isEs ? 'Lista catálogo (paginado)' : 'List catalog (paginated)'}</div>
          <div><span className="text-emerald-700">GET</span> <code className="text-slate-700">{`${API}/external/products.xlsx`}</code> — {isEs ? 'Descarga Excel completo' : 'Excel download'}</div>
          <div><span className="text-emerald-700">GET</span> <code className="text-slate-700">{`${API}/external/orders`}</code> — {isEs ? 'Listar pedidos propios' : 'List own orders'}</div>
          <div><span className="text-amber-700">POST</span> <code className="text-slate-700">{`${API}/external/orders`}</code> — {isEs ? 'Crear pedido' : 'Create order'}</div>
          <div><span className="text-blue-700">PUT</span> <code className="text-slate-700">{`${API}/external/orders/{id}`}</code> — {isEs ? 'Actualizar pedido (solo si está en estado Pendiente)' : 'Update order (only while Pending)'}</div>
          <div className="pt-2 text-xs text-muted-foreground font-sans">
            {isEs
              ? 'Autenticación: cabecera Authorization: Bearer kpb_xxx · Pulsa "Ver guía completa" para ejemplos curl/JavaScript/Python listos para copiar.'
              : 'Auth header Authorization: Bearer kpb_xxx · Click "Full guide" for copy-ready curl/JavaScript/Python examples.'}
          </div>
        </CardContent>
      </Card>

      {/* Documentation dialog */}
      <DocsDialog
        open={showDocs}
        onOpenChange={(o) => { setShowDocs(o); if (!o) setTestResp(''); }}
        isEs={isEs}
        API={API}
        docsKey={docsKey}
        setDocsKey={setDocsKey}
        copy={copyKey}
        testResp={testResp}
        setTestResp={setTestResp}
        testLoading={testLoading}
        setTestLoading={setTestLoading}
      />

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva clave de socio</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre del socio *</Label>
              <Input
                value={newKey.name}
                onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                placeholder="Pablo Vaselice"
                data-testid="api-key-name"
              />
            </div>
            <div>
              <Label className="text-xs">Email del socio *</Label>
              <Input
                type="email"
                value={newKey.partner_email}
                onChange={(e) => setNewKey({ ...newKey, partner_email: e.target.value })}
                placeholder="pablo@purolink.com"
                data-testid="api-key-email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Aquí se enviará automáticamente el enlace de acceso al portal.
              </p>
            </div>
            <div>
              <Label className="text-xs">Comisión (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={newKey.commission_rate}
                onChange={(e) => setNewKey({ ...newKey, commission_rate: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label className="text-xs">Notas internas</Label>
              <Textarea
                rows={2}
                value={newKey.notes}
                onChange={(e) => setNewKey({ ...newKey, notes: e.target.value })}
                placeholder="P.ej. acuerdo verbal del 12/06, comisión a revisar en julio"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleOpenWarning} data-testid="create-key-continue-btn">Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WARNING dialog before sending email */}
      <Dialog open={showWarning} onOpenChange={(o) => { if (!creating) setShowWarning(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Confirma antes de enviar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded-md border-2 border-rose-300 bg-rose-50 text-rose-900">
              <div className="font-semibold mb-1">⚠️ Esta API es SOLO para socios, no para clientes finales.</div>
              <div className="text-xs">
                El catálogo incluye datos confidenciales (PC, precios premium, márgenes). Compártelo únicamente con socios de confianza con los que tengas un acuerdo comercial firmado. <b>Nunca</b> con clientes finales ni terceros.
              </div>
            </div>
            <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-1">
              <div><span className="text-muted-foreground">Socio:</span> <b>{newKey.name}</b></div>
              <div><span className="text-muted-foreground">Email destino:</span> <b className="font-mono text-xs">{newKey.partner_email}</b></div>
              <div><span className="text-muted-foreground">Comisión:</span> <b>{newKey.commission_rate}%</b></div>
            </div>
            <p className="text-xs text-muted-foreground">
              Al pulsar "Crear y enviar" se generará una clave de acceso única y se enviará automáticamente al email indicado, junto con el enlace al portal.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWarning(false)} disabled={creating}>Volver</Button>
            <Button
              onClick={handleConfirmAndSend}
              disabled={creating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-send-key-btn"
            >
              {creating ? 'Enviando…' : 'Crear y enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal new key (only once) */}
      <Dialog open={!!revealedKey} onOpenChange={(o) => { if (!o) setRevealedKey(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-amber-500" />
              Clave creada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm flex gap-2">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <span>
                Esta es la única vez que verás la clave completa. Por seguridad no se almacena en claro: si la pierdes habrá que generar una nueva.
              </span>
            </div>
            <div>
              <Label className="text-xs">Socio</Label>
              <div className="font-medium">{revealedKey?.name}</div>
            </div>

            {/* Email status */}
            {revealedKey?.email_sent ? (
              <div className="p-3 rounded-md border-2 border-emerald-300 bg-emerald-50 text-emerald-900 text-sm">
                <div className="font-semibold">✅ Email enviado correctamente</div>
                <div className="text-xs mt-1">
                  Se ha enviado el enlace de acceso y la clave a <b className="font-mono">{revealedKey?.partner_email}</b>. El socio recibirá el correo en unos segundos.
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md border-2 border-rose-300 bg-rose-50 text-rose-900 text-sm">
                <div className="font-semibold">⚠ Email NO enviado</div>
                <div className="text-xs mt-1">
                  {revealedKey?.email_error || 'No se pudo enviar el email automáticamente.'} Copia el enlace de abajo y envíaselo manualmente al socio.
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Clave (API key)</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all bg-slate-100 p-2 rounded text-xs font-mono select-all" data-testid="revealed-api-key">
                  {revealedKey?.raw_key}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyKey(revealedKey?.raw_key)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="pt-2 border-t">
              <Label className="text-xs">Enlace de acceso directo al portal (1-click)</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all bg-emerald-50 border border-emerald-200 p-2 rounded text-xs font-mono select-all" data-testid="revealed-partner-link">
                  {`${window.location.origin}/partner?key=${revealedKey?.raw_key || ''}`}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyKey(`${window.location.origin}/partner?key=${revealedKey?.raw_key || ''}`)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Este enlace ya se ha enviado al socio por email. Cópialo solo si necesitas reenviárselo manualmente.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)}>Hecho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeysTab;

const CodeBlock = ({ children }) => (
  <pre className="bg-slate-900 text-emerald-100 text-xs p-3 rounded-md overflow-x-auto whitespace-pre font-mono leading-relaxed">{children}</pre>
);

const CopyButton = ({ text, copy, isEs }) => (
  <Button size="sm" variant="outline" className="h-7" onClick={() => copy(text)}>
    <Copy className="h-3 w-3 mr-1" /> {isEs ? 'Copiar' : 'Copy'}
  </Button>
);

// ===========================================================
// DocsDialog — comprehensive partner integration guide
// ===========================================================
const DocsDialog = ({ open, onOpenChange, isEs, API, docsKey, setDocsKey, copy, testResp, setTestResp, testLoading, setTestLoading }) => {
  const k = docsKey || 'kpb_TU_API_KEY_AQUI';

  const curlList = `curl -X GET "${API}/external/products?limit=10&country=Spain&dr_min=30" \\
  -H "Authorization: Bearer ${k}"`;

  const curlXlsx = `curl -L -o catalogo.xlsx \\
  "${API}/external/products.xlsx?country=Spain" \\
  -H "Authorization: Bearer ${k}"`;

  const curlCreate = `curl -X POST "${API}/external/orders" \\
  -H "Authorization: Bearer ${k}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_website": "techemprende.com",
    "customer_name": "Cliente Final SL",
    "customer_email": "info@cliente.com",
    "anchor_text": "comprar zapatillas",
    "target_url": "https://miecommerce.com/zapatillas",
    "external_order_number": "REF-2026-001",
    "notes": "Publicar antes del 30/06"
  }'`;

  const curlListOrders = `curl -X GET "${API}/external/orders?page=1&limit=50" \\
  -H "Authorization: Bearer ${k}"`;

  const curlUpdate = `curl -X PUT "${API}/external/orders/{order_id}" \\
  -H "Authorization: Bearer ${k}" \\
  -H "Content-Type: application/json" \\
  -d '{"anchor_text": "nuevo texto ancla", "target_url": "https://nueva-url.com"}'`;

  const jsExample = `// Node.js / Browser (fetch)
const API_KEY = "${k}";
const BASE = "${API}";

// 1) Listar catálogo filtrado
const productos = await fetch(\`\${BASE}/external/products?country=Spain&dr_min=30&limit=100\`, {
  headers: { "Authorization": \`Bearer \${API_KEY}\` }
}).then(r => r.json());
console.log(productos.total, "productos");

// 2) Crear pedido
const nuevo = await fetch(\`\${BASE}/external/orders\`, {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${API_KEY}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    product_website: "techemprende.com",
    customer_name: "Acme SL",
    customer_email: "info@acme.com",
    anchor_text: "comprar zapatillas",
    target_url: "https://acme.com/zapatillas"
  })
}).then(r => r.json());
console.log("Pedido creado:", nuevo.order_number);`;

  const pyExample = `# Python (requests)
import requests

API_KEY = "${k}"
BASE = "${API}"
HEAD = {"Authorization": f"Bearer {API_KEY}"}

# 1) Catálogo
r = requests.get(f"{BASE}/external/products",
                 params={"country": "Spain", "dr_min": 30, "limit": 100},
                 headers=HEAD)
print(r.json()["total"], "productos")

# 2) Crear pedido
order = requests.post(f"{BASE}/external/orders", headers=HEAD, json={
    "product_website": "techemprende.com",
    "customer_name": "Acme SL",
    "customer_email": "info@acme.com",
    "anchor_text": "comprar zapatillas",
    "target_url": "https://acme.com/zapatillas",
}).json()
print("Pedido creado:", order["order_number"])`;

  const tryIt = async () => {
    if (!docsKey || !docsKey.startsWith('kpb_')) {
      setTestResp(isEs
        ? 'Pega arriba una API key válida (empieza por kpb_)'
        : 'Paste a valid API key above (must start with kpb_)');
      return;
    }
    setTestLoading(true);
    setTestResp('...');
    try {
      const r = await fetch(`${API}/external/products?limit=2`, {
        headers: { Authorization: `Bearer ${docsKey}` },
      });
      const j = await r.json();
      setTestResp(JSON.stringify(j, null, 2).slice(0, 4000));
    } catch (e) {
      setTestResp(String(e));
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            {isEs ? 'Guía de integración para partners' : 'Partner integration guide'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* API key input */}
          <div>
            <Label className="text-xs font-medium">
              {isEs ? 'Tu API key (los ejemplos se actualizan en vivo)' : 'Your API key (examples update live)'}
            </Label>
            <div className="flex gap-2 items-center">
              <Input
                value={docsKey}
                onChange={(e) => setDocsKey(e.target.value)}
                placeholder="kpb_xxxxxxxxxxxxxxxx"
                className="font-mono text-xs"
                data-testid="docs-key-input"
              />
              <Button size="sm" variant="outline" onClick={tryIt} disabled={testLoading} data-testid="docs-test-btn">
                <Play className="h-3 w-3 mr-1" />
                {isEs ? 'Probar' : 'Test'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isEs
                ? 'Si no tienes una key, ciérralo y pulsa "Nueva Key". Luego vuelve aquí.'
                : "If you don't have a key yet, close this and click \"New Key\", then come back."}
            </p>
            {testResp && (
              <pre className="mt-2 bg-slate-50 border border-slate-200 text-slate-700 text-xs p-2 rounded max-h-40 overflow-auto">{testResp}</pre>
            )}
          </div>

          <Tabs defaultValue="curl">
            <TabsList>
              <TabsTrigger value="curl">curl</TabsTrigger>
              <TabsTrigger value="js">JavaScript</TabsTrigger>
              <TabsTrigger value="py">Python</TabsTrigger>
              <TabsTrigger value="reference">{isEs ? 'Referencia' : 'Reference'}</TabsTrigger>
            </TabsList>

            <TabsContent value="curl" className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">1. {isEs ? 'Listar catálogo (JSON paginado)' : 'List catalog (paginated JSON)'}</span>
                  <CopyButton text={curlList} copy={copy} isEs={isEs} />
                </div>
                <CodeBlock>{curlList}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">2. {isEs ? 'Descargar catálogo completo (Excel)' : 'Download full catalog (Excel)'}</span>
                  <CopyButton text={curlXlsx} copy={copy} isEs={isEs} />
                </div>
                <CodeBlock>{curlXlsx}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">3. {isEs ? 'Crear pedido' : 'Create order'}</span>
                  <CopyButton text={curlCreate} copy={copy} isEs={isEs} />
                </div>
                <CodeBlock>{curlCreate}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">4. {isEs ? 'Listar mis pedidos' : 'List my orders'}</span>
                  <CopyButton text={curlListOrders} copy={copy} isEs={isEs} />
                </div>
                <CodeBlock>{curlListOrders}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">5. {isEs ? 'Actualizar pedido (solo si Pendiente)' : 'Update order (only while Pending)'}</span>
                  <CopyButton text={curlUpdate} copy={copy} isEs={isEs} />
                </div>
                <CodeBlock>{curlUpdate}</CodeBlock>
              </div>
            </TabsContent>

            <TabsContent value="js">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{isEs ? 'Ejemplo completo (fetch)' : 'Complete example (fetch)'}</span>
                <CopyButton text={jsExample} copy={copy} isEs={isEs} />
              </div>
              <CodeBlock>{jsExample}</CodeBlock>
            </TabsContent>

            <TabsContent value="py">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{isEs ? 'Ejemplo completo (requests)' : 'Complete example (requests)'}</span>
                <CopyButton text={pyExample} copy={copy} isEs={isEs} />
              </div>
              <CodeBlock>{pyExample}</CodeBlock>
            </TabsContent>

            <TabsContent value="reference" className="text-sm space-y-3">
              <div>
                <div className="font-semibold mb-1">{isEs ? 'Filtros de /external/products' : 'Filters for /external/products'}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  <li><code className="bg-slate-100 px-1 rounded">country</code> — {isEs ? 'país (contiene, case-insensitive). Ej: Spain' : 'country (contains, case-insensitive)'}</li>
                  <li><code className="bg-slate-100 px-1 rounded">language</code> — {isEs ? 'idioma exacto. Ej: ES' : 'exact language code'}</li>
                  <li><code className="bg-slate-100 px-1 rounded">dr_min</code> / <code className="bg-slate-100 px-1 rounded">dr_max</code> — {isEs ? 'rango Domain Rating' : 'Domain Rating range'}</li>
                  <li><code className="bg-slate-100 px-1 rounded">updated_since</code> — ISO-8601 (YYYY-MM-DD)</li>
                  <li><code className="bg-slate-100 px-1 rounded">page</code> / <code className="bg-slate-100 px-1 rounded">limit</code> — {isEs ? 'paginación (limit máx 5000)' : 'pagination (limit max 5000)'}</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold mb-1">{isEs ? 'Campos por producto' : 'Product fields'}</div>
                <div className="text-xs font-mono bg-slate-50 p-2 rounded border">
                  website, country, category, pvp, pc, premium_purchase_price, premium_sale_price, allow_purchase, allow_by_admin, dr, traffic, language, site_type, link_type, link_duration, ad_label, casino, cbd, gambling, cryptocurrency, sex, erotic, vpn, tarot, cannabis, created_at, updated_at
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">{isEs ? 'Códigos de error' : 'Error codes'}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  <li><b>401</b> — {isEs ? 'API key ausente, inválida o revocada' : 'API key missing, invalid or revoked'}</li>
                  <li><b>403</b> — {isEs ? 'La key no tiene el scope requerido' : 'API key lacks required scope'}</li>
                  <li><b>404</b> — {isEs ? 'Producto o pedido no encontrado' : 'Product or order not found'}</li>
                  <li><b>409</b> — {isEs ? 'Pedido ya no editable (estado distinto a Pendiente)' : 'Order no longer editable (status not Pending)'}</li>
                </ul>
              </div>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                {isEs
                  ? 'Importante: guarda tu API key en una variable de entorno (.env). Nunca la incluyas en código frontend público.'
                  : 'Important: store your API key in an environment variable (.env). Never expose it in public frontend code.'}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{isEs ? 'Cerrar' : 'Close'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
